import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextAreaField, TextField, ToggleSwitch } from "@/components/base/FormControls";
import { SERVICE_CATEGORY_OPTIONS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { WorkshopService } from "@/types";

interface ServiceFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  service: WorkshopService | null;
}

interface ServiceForm {
  name: string;
  category: string;
  description: string;
  price: string;
  duration_minutes: string;
  active: boolean;
}

const EMPTY: ServiceForm = {
  name: "",
  category: "Montaje",
  description: "",
  price: "",
  duration_minutes: "30",
  active: true,
};

function toNumber(value: string): number | null {
  const parsed = Number.parseFloat(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function toInt(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function ServiceFormModal({
  open,
  onClose,
  onSaved,
  service,
}: ServiceFormModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [form, setForm] = useState<ServiceForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (service) {
      setForm({
        name: service.name,
        category: service.category ?? "Montaje",
        description: service.description ?? "",
        price: service.price != null ? String(service.price) : "",
        duration_minutes: service.duration_minutes != null ? String(service.duration_minutes) : "30",
        active: service.active ?? true,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, service]);

  const update = <K extends keyof ServiceForm>(key: K, value: ServiceForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError("El nombre del servicio es obligatorio.");
      return;
    }
    if (toNumber(form.price) == null) {
      setError("El precio del servicio es obligatorio.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      category: form.category || null,
      description: form.description.trim() || null,
      price: toNumber(form.price),
      duration_minutes: toInt(form.duration_minutes),
      active: form.active,
      updated_at: new Date().toISOString(),
    };

    setSubmitting(true);
    try {
      if (service) {
        const { error: updateError } = await supabase
          .from("services")
          .update(payload)
          .eq("id", service.id);
        if (updateError) {
          setError("No se pudo actualizar el servicio.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "editar",
          entity: "services",
          entityId: service.id,
          branchId: profile?.branch_id ?? null,
          oldValue: { nombre: service.name, precio: service.price },
          newValue: { nombre: payload.name, precio: payload.price },
        });
        toast.success("Servicio actualizado.");
      } else {
        const { data, error: insertError } = await supabase
          .from("services")
          .insert(payload)
          .select("id")
          .single();
        if (insertError || !data) {
          setError("No se pudo crear el servicio.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "crear",
          entity: "services",
          entityId: data.id,
          branchId: profile?.branch_id ?? null,
          newValue: { nombre: payload.name, precio: payload.price },
        });
        toast.success("Servicio creado.");
      }
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={service ? "Editar servicio" : "Nuevo servicio"}
      subtitle="Los servicios del taller se pueden vender junto a los productos en una misma factura."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="service-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {service ? "Guardar cambios" : "Crear servicio"}
          </button>
        </>
      }
    >
      <form id="service-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <TextField
          label="Nombre del servicio"
          name="service_name"
          value={form.name}
          onChange={(value) => update("name", value)}
          placeholder="Ej: Montaje de neumático"
          required
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Categoría"
            name="service_category"
            value={form.category}
            onChange={(value) => update("category", value)}
            options={SERVICE_CATEGORY_OPTIONS}
          />
          <TextField
            label="Precio"
            name="service_price"
            value={form.price}
            onChange={(value) => update("price", value)}
            inputMode="numeric"
            placeholder="0.00"
            required
          />
          <TextField
            label="Duración (minutos)"
            name="service_duration"
            value={form.duration_minutes}
            onChange={(value) => update("duration_minutes", value)}
            inputMode="numeric"
            placeholder="30"
            hint="Tiempo estimado del servicio."
          />
        </div>

        <TextAreaField
          label="Descripción"
          name="service_description"
          value={form.description}
          onChange={(value) => update("description", value)}
          placeholder="Detalles del servicio y lo que incluye."
          rows={3}
        />

        <ToggleSwitch
          label="Servicio activo"
          description="Los servicios inactivos no aparecen disponibles para vender."
          checked={form.active}
          onChange={(checked) => update("active", checked)}
        />

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}